import React, { useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { sendGeminiMessage } from "../services/geminiService";
import { FaRobot, FaPaperPlane, FaTimes, FaTrash } from "react-icons/fa";
import avatar from "../Assets/chatbot-avatarai.png"; 
import { useContext } from "react";
import { UserContext } from "../Contexts/UserContext";

import { useTranslation } from "react-i18next";
import { toast } from "react-toastify"; 



const ChatAI = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role: 'user'|'model', text: ''}
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const chatAreaRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  // Inject custom scrollbar style ONCE
  React.useEffect(() => {
    if (!document.getElementById('chat-ai-scrollbar-style')) {
      const style = document.createElement('style');
      style.id = 'chat-ai-scrollbar-style';
      style.innerHTML = `
        .chat-ai-scroll::-webkit-scrollbar, textarea.chat-ai-scroll::-webkit-scrollbar {
          width: 6px;
        }
        .chat-ai-scroll::-webkit-scrollbar-thumb, textarea.chat-ai-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #60a5fa 40%, #38bdf8 100%);
          border-radius: 8px;
        }
        .chat-ai-scroll::-webkit-scrollbar-track, textarea.chat-ai-scroll::-webkit-scrollbar-track {
          background: #e0f2fe;
          border-radius: 8px;
        }
        .chat-ai-scroll, textarea.chat-ai-scroll {
          scrollbar-width: thin;
          scrollbar-color: #60a5fa #e0f2fe;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);


  React.useEffect(() => {
    const checkOverflow = () => {
      if (chatAreaRef.current) {
        setIsOverflowing(chatAreaRef.current.scrollHeight > chatAreaRef.current.clientHeight + 2);
      }
    };
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [messages]);

  // Auto-resize textarea
  const handleInputChange = (e) => {
    setInput(e.target.value);
    if (inputRef.current) {
      inputRef.current.style.height = '50px'; // reset to min height
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 128) + 'px'; // max-h-32 = 128px
    }
  };

  // Handle Enter/Shift+Enter in textarea
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !loading) {
        handleSend(e);
      }
    }
  };

  const [error, setError] = useState("");
  const messagesEndRef = useRef(null);
  const {user} = useContext(UserContext);
  const {t} = useTranslation();
  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setError("");
    // System instruction to keep AI concise (must be 'user' role for Gemini v1beta)
    const systemMsg = { role: "user", text: "Instruction: Always respond concisely and strictly to the point. Do not elaborate or add unnecessary information." };
    const chatHistory = messages.length > 0 && messages[0].text.startsWith("Instruction:") ? messages : [systemMsg, ...messages];
    const userMsg = { role: "user", text: input };
    setMessages((prev) => [...chatHistory, userMsg]);
    setInput("");
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = '50px';
    }
    setLoading(true);
    try {
      const aiReply = await sendGeminiMessage(input, [...chatHistory, userMsg]);
      setMessages((prev) => [...chatHistory, userMsg, { role: "model", text: aiReply }]);
    } catch (err) {
      setError(err.message || "Error contacting Gemini API.");
    } finally {
      setLoading(false);
    }
  };


  // Reset chat handler
  const handleReset = () => {
    setMessages([]);
    setError("");
    setInput("");
  };

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <>
      {/* Floating Chatbot Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="group fixed bottom-3 right-3 w-16 h-16 rounded-full bg-white border-blue-600 border-2 shadow-lg flex items-center justify-center z-[999] cursor-pointer transition-opacity duration-200 overflow-visible"
          aria-label={t('chat.open')}
          style={{position:'fixed'}}
        >
          {/* Hover Waves */}
          <span className="absolute w-16 h-16 rounded-full bg-blue-200 opacity-0 group-hover:opacity-60 group-hover:scale-110 transition-all duration-500 z-[-2]" style={{inset:0,margin:'auto'}}></span>
          <span className="absolute w-[68px] h-[68px] rounded-full bg-blue-300 opacity-0 group-hover:opacity-40 group-hover:scale-125 transition-all duration-700 z-[-3]" style={{inset:"-8px",margin:'auto'}}></span>
          <span className="absolute w-[50px] h-[50px] rounded-full bg-blue-400 opacity-0 group-hover:opacity-30 group-hover:scale-150 transition-all duration-1000 z-[-4]" style={{inset:"-16px",margin:'auto'}}></span>
          <img src={avatar} alt="Chatbot Avatar" className="w-12 h-12 rounded-full object-cover relative z-[999]" />
        </button>
      )}
      {open && (
        <>
          {/* Overlay for click-outside close */}
          <div
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/5 z-[10000]"
          />
          {/* Close Button (over bot icon position) */}
          <button
            onClick={() => setOpen(false)}
            className="fixed bottom-8 right-8 w-16 h-16 rounded-full bg-red-600 border-none shadow-lg flex items-center justify-center z-[10001] cursor-pointer transition-opacity duration-200"
            aria-label={t('chat.close')}
          >
            <FaTimes size={36} color="#fff" />
          </button>
          {/* Modal positioned to the left of the floating button */}
          <div className="fixed bottom-8 right-[104px] max-w-[370px] w-[90vw] z-[10002] bg-white rounded-2xl shadow-2xl overflow-hidden p-0 transition-all duration-200">
            {/* Header */}
            <div className="bg-gradient-to-br from-blue-700 to-cyan-400 p-6 pb-4 flex items-center gap-4 rounded-t-2xl relative">
          {/* Avatar and Name */}
          <div className="w-14 h-14 bg-blue-100 text-blue-700 flex items-center justify-center rounded-full text-2xl font-bold">
            {user.firstName?.[0]}
            {user.lastName?.[0]}
          </div>
          <div>
            <h2 className="font-bold text-xl mb-1 text-white">{user.firstName} {user.lastName}</h2>
            <h4 className="font-semibold text-sm text-blue-100">{user.username}</h4>
            <div className="text-blue-100 text-[13px] mt-0.5">{t('chat.online')}</div>
          </div>
        </div>

          {/* Chat area */}
          <div
              ref={chatAreaRef}
              className="h-80 overflow-y-auto bg-gray-100 p-4 pt-6 pb-0 rounded-b-2xl transition-all duration-200 chat-ai-scroll"
            >
            {messages.length === 0 && <div className="text-gray-500 text-center mt-8">{t('chat.startConversation')}</div>}
            {messages.map((msg, i) => (
              <div key={i} className={`flex min-w-0 ${msg.role === "user" ? "justify-end" : "justify-start"} my-2`}>
                {msg.role === "model" ? (
                  <div className="bg-gray-200 text-gray-900 ml-2 mr-2 py-2.5 px-4 rounded-2xl text-sm font-medium max-w-full break-words whitespace-pre-line text-left rounded-br-2xl rounded-bl-md flex-shrink-0">
                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="bg-blue-500 text-white ml-2 mr-2 py-2.5 px-4 rounded-2xl text-sm font-medium max-w-full break-words whitespace-pre-line text-left rounded-br-md rounded-bl-2xl flex-shrink-0">{msg.text}</div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          {/* Input area */}
          <form onSubmit={handleSend} className="flex items-center gap-2 bg-white p-4 rounded-b-2xl shadow-md">
            <textarea
                ref={inputRef}
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                disabled={loading}
                placeholder={t('chat.placeholder')}
                className="flex-1 h-[50px] px-4 py-3 rounded-full border border-gray-200 text-sm outline-none bg-gray-100 resize-none chat-ai-scroll"
                rows={1}
                style={{minHeight: 44, maxHeight: 128}}
              />
            <button
                type="submit"
                disabled={loading || !input.trim()}
                className={`w-11 h-11 rounded-full bg-blue-500 border-none flex items-center justify-center shadow-md ${loading || !input.trim() ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                aria-label="Send"
              >
              <FaPaperPlane size={20} color="#fff" />
            </button>
            {(messages.filter(m => m.role !== "system").length > 0) && (
              <button
                type="button"
                onClick={handleReset}
                disabled={loading}
                className={`w-11 h-11 rounded-full bg-gradient-to-br from-red-600 to-pink-500 border-none flex items-center justify-center shadow-md ${loading ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                aria-label="Reset Chat"
              >
                <FaTrash size={20} color="#fff" />
              </button>
            )}
          </form>
          {/* Error message */}
          {error && <div className="text-red-600 mx-4 mt-2 text-xs">{error}</div>}
        </div>
        </>
      )}
    </>
  );
};

export default ChatAI;
